import {
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
  NormalizedCacheObject,
  createHttpLink,
  from,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { signOut, useSession } from 'next-auth/react';
import { PropsWithChildren, useEffect, useMemo } from 'react';
import { AddToast, useToasts } from 'react-toast-notifications';
import * as uuid from 'uuid';
import { correlator } from '../../diagnostics/client';
import introspection from '../../generated/fragmentTypes.json';
import { getVisitorCode } from '../../experimentation/client';

let authToken: string;

const httpLink = createHttpLink({
  uri: '/api/graphql',
});

const authLink = setContext(async (_, { headers }) => ({
  headers: {
    ...headers,
    authorization: authToken ? `Bearer ${authToken}` : '',
    // This will be the correlation id of the active withDataPoint chain.
    // If the GraphQL query/mutation request is sent outside of withDataPoint,
    // we will generate a new one for the sake of correlating this frontend call
    // with the backend responder.
    'X-Correlation-ID': correlator.getCurrentId() ?? uuid.v4(),
    'X-Visitor-Code': await getVisitorCode(),
  },
}));

let apolloClient;

function createApolloClient(addToast: AddToast) {
  const errorLink = onError(({ graphQLErrors }) => {
    if (graphQLErrors)
      graphQLErrors.forEach(({ message }) => {
        if (message === 'Not Authorised!') {
          addToast('Your session has expired, you are being logged out.', {
            appearance: 'info',
          });
          if (document.URL.includes('/auth/verified-email')) {
            void signOut({ callbackUrl: '/assessment' });
          } else {
            void signOut();
          }
        }
      });
  });
  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: from([authLink, errorLink, httpLink]),
    cache: new InMemoryCache({
      possibleTypes: introspection.possibleTypes,
      typePolicies: {
        Specialty: { keyFields: false },
      },
    }),
  });
}

export function initializeApollo(addToast: AddToast, initialState = null) {
  const _apolloClient = apolloClient ?? createApolloClient(addToast);

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    // Get existing cache, loaded during client side data fetching
    const existingCache = _apolloClient.extract();
    // Restore the cache using the data passed from getStaticProps/getServerSideProps
    // combined with the existing cached data
    _apolloClient.cache.restore({ ...existingCache, ...initialState });
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return _apolloClient;
  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = _apolloClient;
  return _apolloClient;
}

export function getApolloClient(): ApolloClient<NormalizedCacheObject> {
  return apolloClient;
}

type PropTypes = PropsWithChildren<{ initialState: any }>;

const GraphqlProvider = ({ children, initialState }: PropTypes) => {
  const { data: session } = useSession();
  const { addToast } = useToasts();
  const client = useMemo(
    () => initializeApollo(addToast, initialState),
    [initialState]
  );
  // @ts-ignore
  const accessToken: string | undefined = session?.accessToken as
    | string
    | undefined;
  useEffect(() => {
    authToken = accessToken;
  }, [accessToken]);
  return <ApolloProvider client={client}>{children}</ApolloProvider>;
};

export default GraphqlProvider;
